import type { XYEffect } from "../engine/effects/xyEffect";
import type { CanvasLayer, FrameContext } from "./canvasLayer";
import { resolveXYEffectDrawPosition } from "./movementInterpolation";
import type { ManagedAnimation } from "./sprites/managedAnimation";

interface ActiveEffect {
	effect: XYEffect;
	anim: ManagedAnimation;
}

/**
 * Plays "unbounded" sprite effects: they animate without blocking the turn and persist
 * across turns until they finish (or a level transition clears them) — the sprite
 * analogue of the particle system. Playback only; UnboundedEffectQueue handles
 * preloading and order-synced release.
 */
export class UnboundedEffectRenderer implements CanvasLayer {
	private active: ActiveEffect[] = [];

	play(effect: XYEffect, anim: ManagedAnimation): void {
		this.active.push({ effect, anim });
	}

	update(elapsedMs: number): void {
		for (const entry of this.active) {
			entry.anim.passTime(elapsedMs);
		}
		this.active = this.active.filter((e) => !e.anim.isExpired());
	}

	render(ctx: CanvasRenderingContext2D, frame: FrameContext): void {
		for (const { effect, anim } of this.active) {
			const sprite = anim.getCurrentSprite();
			if (!sprite.image) continue;
			const drawXY = resolveXYEffectDrawPosition(effect, anim.getTotalElapsedTime());
			const pos = frame.camera.boardToPixelWithSprite(drawXY, sprite);
			ctx.drawImage(sprite.image, pos.x, pos.y);
		}
	}

	clear(): void {
		this.active = [];
	}
}
